function titleCase(value) {
  return String(value || "")
    .replaceAll("_", " ")
    .trim()
    .replace(/\b\w/g, (match) => match.toUpperCase());
}

function priorityTone(priority) {
  if (priority === "high") {
    return "overdue";
  }
  if (priority === "normal") {
    return "stale";
  }
  return "muted";
}

function leadTone(lead, lane) {
  if (!lead) {
    return "muted";
  }
  if (lead.status === "dead" || (lane && lane.is_lost)) {
    return "overdue";
  }
  if ((lane && lane.is_closed) || lead.status === "closed") {
    return "stale";
  }
  if (lead.status === "active") {
    return "ready";
  }
  return "muted";
}

function mapMembers(members) {
  return new Map(
    (Array.isArray(members) ? members : [])
      .filter((item) => item && item.user_id)
      .map((item) => [
        String(item.user_id),
        {
          fullName: item.full_name || item.email || String(item.user_id),
          role: item.role || null,
        },
      ])
  );
}

function laneForLead(lead, board) {
  if (!lead || !lead.stage_id || !board || !Array.isArray(board.lanes)) {
    return null;
  }
  return board.lanes.find((item) => String(item.stage_id) === String(lead.stage_id)) || null;
}

function peerLanes(lead, board) {
  const lanes = Array.isArray((board || {}).lanes) ? board.lanes : [];
  const currentIndex = lanes.findIndex((item) => String(item.stage_id) === String((lead || {}).stage_id || ""));
  return {
    current: currentIndex >= 0 ? lanes[currentIndex] : null,
    previous: currentIndex > 0 ? lanes[currentIndex - 1] : null,
    next: currentIndex >= 0 && currentIndex < lanes.length - 1 ? lanes[currentIndex + 1] : null,
  };
}

export function mergeLeadIntoPipelineBoard(board, lead) {
  if (!board || !lead) {
    return board || null;
  }
  const lanes = Array.isArray(board.lanes) ? board.lanes.map((lane) => ({
    ...lane,
    leads: Array.isArray(lane.leads) ? lane.leads.filter((item) => String(item.id) !== String(lead.id)) : [],
  })) : [];
  const targetLane = lanes.find((lane) => String(lane.stage_id) === String(lead.stage_id || ""));
  if (targetLane) {
    targetLane.leads = [lead].concat(targetLane.leads || []);
  }
  const normalized = lanes.map((lane) => ({
    ...lane,
    lead_count: Array.isArray(lane.leads) ? lane.leads.length : Number(lane.lead_count || 0),
  }));
  return {
    ...board,
    lanes: normalized,
    lane_count: normalized.length,
    total_leads: normalized.reduce((total, lane) => total + Number(lane.lead_count || 0), 0),
  };
}

export function buildRecordPipelineWorkspace(options = {}) {
  const settings = Object.assign(
    {
      conversation: null,
      lead: null,
      pipelineBoard: null,
      relatedConversations: [],
      members: [],
    },
    options
  );
  const conversation = settings.conversation || {};
  const lead = settings.lead || null;
  const relatedConversations = Array.isArray(settings.relatedConversations) ? settings.relatedConversations : [];
  const pipelineBoard = settings.pipelineBoard || null;
  const memberMap = mapMembers(settings.members);
  const lane = laneForLead(lead, pipelineBoard);
  const peers = peerLanes(lead, pipelineBoard);
  const owner = lead && lead.assigned_user_id ? memberMap.get(String(lead.assigned_user_id)) : null;
  const ownerLabel = owner ? `${owner.fullName}${owner.role ? ` - ${owner.role}` : ""}` : lead && lead.assigned_user_id ? "Assigned teammate" : "Unassigned";
  const stageLabel = lane ? lane.stage_name : lead && lead.stage_status_label ? lead.stage_status_label : "Unstaged";
  const recordTone = leadTone(lead, lane);

  const navigationRows = relatedConversations.length
    ? relatedConversations.slice(0, 6).map((item) => ({
        conversationId: item.id,
        label: item.id === conversation.id ? "Current thread" : item.contact_name || "Related thread",
        value: item.aging_bucket || item.status || "open",
        tone: item.id === conversation.id ? "ready" : item.status === "closed" ? "stale" : "muted",
        meta: `${item.assigned_user_id ? "Assigned" : "Unassigned"} | ${item.last_message_at || item.created_at || "No activity timestamp"}`,
        actionLabel: item.id === conversation.id ? "Viewing" : "Open",
        current: item.id === conversation.id,
      }))
    : [
        {
          conversationId: null,
          label: "Record navigation",
          value: "No additional threads",
          tone: "muted",
          meta: "This contact does not have other conversation records yet.",
          actionLabel: "",
          current: false,
        },
      ];

  if (!lead) {
    return {
      summaryCards: [
        {
          label: "Lead record",
          value: "Not created",
          detail: "The CRM has not created a lead record for this contact yet.",
          tone: "muted",
        },
        {
          label: "Pipeline lane",
          value: "Unstaged",
          detail: "A lead record is required before pipeline progression can be tracked.",
          tone: "muted",
        },
        {
          label: "Related threads",
          value: String(Math.max(relatedConversations.length - 1, 0)),
          detail: relatedConversations.length > 1 ? "Additional conversation history is available." : "No extra thread history yet.",
          tone: relatedConversations.length > 1 ? "ready" : "muted",
        },
      ],
      leadRows: [
        {
          label: "Record status",
          value: "Lead unavailable",
          tone: "muted",
          meta: "Create a lead-backed action, such as an internal note, to establish CRM record state.",
        },
        {
          label: "Pipeline posture",
          value: "Not started",
          tone: "muted",
          meta: "Stage progression becomes available once the record has a lead id.",
        },
      ],
      progressRows: [
        {
          label: "Current lane",
          value: "No active lane",
          tone: "muted",
          meta: "The record has not been attached to the tenant pipeline yet.",
        },
        {
          label: "Pipeline coverage",
          value: pipelineBoard ? `${pipelineBoard.total_leads || 0} records` : "Board unavailable",
          tone: pipelineBoard ? "ready" : "muted",
          meta: pipelineBoard ? `${pipelineBoard.lane_count || 0} configured lanes` : "Pipeline board could not be loaded for this record.",
        },
      ],
      navigationRows,
    };
  }

  const statusLabel = lead.stage_status_label || titleCase(lead.stage_status_key || "") || titleCase(lead.status) || "Active";
  const lastTouchMeta = lead.last_contacted_at
    ? `Last contacted ${lead.last_contacted_at}`
    : lead.last_response_at
      ? `Last response ${lead.last_response_at}`
      : "No contact timestamps recorded yet.";

  return {
    summaryCards: [
      {
        label: "Current lane",
        value: stageLabel,
        detail: lane ? `${lane.lead_count} record(s) currently in this lane.` : "This record is active but not attached to a configured lane.",
        tone: recordTone,
      },
      {
        label: "Record posture",
        value: statusLabel,
        detail: lead.dead_reason ? `Dead reason: ${titleCase(lead.dead_reason)}` : `${titleCase(lead.status)} lead state`,
        tone: recordTone,
      },
      {
        label: "Priority",
        value: titleCase(lead.priority || "normal"),
        detail: ownerLabel,
        tone: priorityTone(lead.priority || "normal"),
      },
    ],
    leadRows: [
      {
        label: "Record status",
        value: titleCase(lead.status || "active"),
        tone: recordTone,
        meta: lead.dead_reason ? `Reason: ${titleCase(lead.dead_reason)}` : `Source: ${lead.source || "manual"}`,
      },
      {
        label: "Stage posture",
        value: statusLabel,
        tone: recordTone,
        meta: lane ? `${lane.stage_name} lane` : "This lead has a stage id that is not currently mapped to the pipeline board.",
      },
      {
        label: "Record owner",
        value: owner ? owner.fullName : "Unassigned",
        tone: owner ? "ready" : "stale",
        meta: owner && owner.role ? owner.role : "No active assignee is attached to this record.",
      },
      {
        label: "Last touchpoint",
        value: lead.last_contacted_at ? "Contacted" : lead.last_response_at ? "Responded" : "No touchpoint",
        tone: lead.last_contacted_at ? "ready" : lead.last_response_at ? "stale" : "muted",
        meta: lastTouchMeta,
      },
    ],
    progressRows: [
      {
        label: "Previous lane",
        value: peers.previous ? peers.previous.stage_name : "Start of pipeline",
        tone: peers.previous ? "muted" : "ready",
        meta: peers.previous ? `${peers.previous.lead_count} record(s)` : "No prior lane before the current stage.",
      },
      {
        label: "Current lane",
        value: peers.current ? peers.current.stage_name : stageLabel,
        tone: recordTone,
        meta: peers.current ? `${peers.current.lead_count} record(s) in this lane` : "Current lane is not available on the board snapshot.",
      },
      {
        label: "Next lane",
        value: peers.next ? peers.next.stage_name : lane && lane.is_closed ? "Closed outcome" : "No next lane",
        tone: peers.next ? "ready" : "muted",
        meta: peers.next ? `${peers.next.lead_count} record(s)` : lane && lane.is_closed ? "This record is already in a terminal lane." : "No next stage follows the current lane.",
      },
      {
        label: "Pipeline coverage",
        value: pipelineBoard ? `${pipelineBoard.total_leads || 0} records` : "Board unavailable",
        tone: pipelineBoard ? "ready" : "muted",
        meta: pipelineBoard ? `${pipelineBoard.lane_count || 0} configured lanes` : "Pipeline board could not be loaded for this record.",
      },
    ],
    navigationRows,
  };
}
